import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { History, RotateCcw, Search, Filter, User, FileText, Trash2, Plus, Edit, Eye, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

const ACTION_LABELS = { create: 'Création', update: 'Modification', delete: 'Suppression', revert: 'Annulation', approve: 'Approbation', reject: 'Rejet' };
const ACTION_COLORS = { create: '#059669', update: '#35AFE4', delete: '#dc2626', revert: '#d97706', approve: '#059669', reject: '#dc2626' };
const ACTION_ICONS = { create: Plus, update: Edit, delete: Trash2, revert: RotateCcw, approve: Eye, reject: Trash2 };
const ENTITY_LABELS = { user: 'Membre', formation: 'Formation', article: 'Article', event: 'Événement', election: 'Élection', page: 'Page', patient_article: 'Article patient', conversation: 'Conversation' };

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [detail, setDetail] = useState(null);
  const [reverting, setReverting] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadLogs(); }, [page, filterAction, filterEntity]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '30' });
      if (filterAction) params.append('action', filterAction);
      if (filterEntity) params.append('entity_type', filterEntity);
      const res = await axios.get(`${API}/admin/audit-logs?${params}`, { headers });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch { }
    setLoading(false);
  };

  const revert = async (logId) => {
    if (!window.confirm("Annuler cette action et restaurer l'état précédent ?")) return;
    setReverting(logId);
    try {
      await axios.post(`${API}/admin/audit-logs/${logId}/revert`, {}, { headers });
      toast.success('Action annulée avec succès');
      loadLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'annuler");
    }
    setReverting(null);
  };

  const totalPages = Math.ceil(total / 30);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6" data-testid="audit-log-page">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2"><History size={20} className="text-[#35AFE4]" /> Journal d'audit</h1>
            <p className="text-xs text-gray-500">{total} action{total !== 1 ? 's' : ''} enregistrée{total !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(0); }}
              className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white" data-testid="filter-action">
              <option value="">Toutes les actions</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filterEntity} onChange={e => { setFilterEntity(e.target.value); setPage(0); }}
              className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white" data-testid="filter-entity">
              <option value="">Tous les types</option>
              {Object.entries(ENTITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin text-gray-400" /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">Aucune action enregistrée</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {logs.map(log => {
                const Icon = ACTION_ICONS[log.action] || Edit;
                const color = ACTION_COLORS[log.action] || '#64748b';
                return (
                  <div key={log.id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" data-testid={`log-${log.id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${color}15` }}>
                      <Icon size={14} style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color, backgroundColor: `${color}15` }}>
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                        <span className="text-[10px] text-gray-400">{ENTITY_LABELS[log.entity_type] || log.entity_type}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mt-0.5 truncate">
                        {log.entity_title || log.entity_id}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        par <span className="font-medium text-gray-600">{log.user_name}</span> · {new Date(log.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {log.before && log.action !== 'revert' && (
                      <button onClick={() => revert(log.id)} disabled={reverting === log.id}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors disabled:opacity-50 flex-shrink-0"
                        data-testid={`revert-${log.id}`}>
                        {reverting === log.id ? <Loader2 size={10} className="animate-spin" /> : <RotateCcw size={10} />}
                        Annuler
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="border-t border-gray-100 px-4 py-2.5 flex items-center justify-between">
              <span className="text-[11px] text-gray-400">Page {page + 1} / {totalPages}</span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                  className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"><ChevronLeft size={14} /></button>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                  className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
